// ...new file...
export function calculateMetrics(emotionData = {}, userType = 'student') {
  // Expect percentages (0-100)
  const calm = Number(emotionData.calm || 0);
  const stressed = Number(emotionData.stressed || 0);
  const anxious = Number(emotionData.anxious || 0);
  const hurt = Number(emotionData.hurt || 0);
  const demotivated = Number(emotionData.demotivated || 0);
  const happy = Number(emotionData.happy || 0);

  // Reusable burnout calculation (same formula for student & professional)
  const stressedContrib = stressed * 0.4;
  const anxiousContrib = anxious * 0.3;
  const hurtContrib = hurt * 0.15;
  const demotivatedContrib = demotivated * 0.15;
  const burnoutRaw = stressedContrib + anxiousContrib + hurtContrib + demotivatedContrib;
  const burnoutScore = Math.max(0, Math.min(100, burnoutRaw));
  const burnoutRounded = Math.round(burnoutScore);

  // Student metrics (existing)
  if (userType === 'student') {
    // risk bucket -> percentage mapping (bar)
    let riskLevel = 'Low';
    let riskPercentage = 0;
    if (burnoutScore <= 30) {
      riskLevel = 'Low';
      riskPercentage = (burnoutScore / 30) * 20;
    } else if (burnoutScore <= 60) {
      riskLevel = 'Moderate';
      riskPercentage = 20 + ((burnoutScore - 30) / 30) * 30;
    } else {
      riskLevel = 'High';
      riskPercentage = 50 + ((burnoutScore - 60) / 40) * 50;
    }
    riskPercentage = Math.max(0, Math.min(100, Math.round(riskPercentage)));

    // behavioral flags (student)
    const flags = [];
    if (stressed > 70 && anxious > 50) flags.push({ type: 'Bullying Alert', severity: 'high' });
    if (demotivated > 60 && calm < 20) flags.push({ type: 'Social Withdrawal', severity: 'medium' });
    if (anxious > 65) flags.push({ type: 'Anxiety Alert', severity: 'medium' });
    if (hurt > 55) flags.push({ type: 'Emotional Distress', severity: 'medium' });
    const flagCount = flags.length;

    // learning readiness (student)
    const positiveScore = (calm * 0.6) + ((happy || calm) * 0.4);
    const negativeScore = (stressed + anxious + demotivated + hurt) / 4;
    let readinessScore = positiveScore - (negativeScore * 0.7);
    readinessScore = Math.max(0, Math.min(100, readinessScore));

    let motivated = 0, partiallyReady = 0, disengaged = 0;
    if (readinessScore >= 60) {
      motivated = Math.round(((readinessScore - 60) / 40) * 100);
      motivated = Math.max(0, Math.min(100, motivated));
      partiallyReady = 100 - motivated;
      disengaged = 0;
    } else if (readinessScore >= 30) {
      partiallyReady = Math.round(((readinessScore - 30) / 30) * 100);
      partiallyReady = Math.max(0, Math.min(100, partiallyReady));
      motivated = 0;
      disengaged = 100 - partiallyReady;
    } else {
      disengaged = Math.round(((30 - readinessScore) / 30) * 100);
      disengaged = Math.max(0, Math.min(100, disengaged));
      motivated = 0;
      partiallyReady = 100 - disengaged;
    }

    return {
      type: 'student',
      burnout: {
        score: burnoutRounded,
        level: riskLevel,
        percentage: riskPercentage,
        breakdown: {
          stressed, stressedContrib: Number((stressedContrib).toFixed(2)),
          anxious, anxiousContrib: Number((anxiousContrib).toFixed(2)),
          hurt, hurtContrib: Number((hurtContrib).toFixed(2)),
          demotivated, demotivatedContrib: Number((demotivatedContrib).toFixed(2)),
          raw: Number(burnoutRaw.toFixed(2))
        }
      },
      behavioral: { flags, count: flagCount, summaryText: `${flagCount} Flags Raised This Week` },
      readiness: { motivated, partiallyReady, disengaged, rawScore: Math.round(readinessScore) }
    };
  }

  // Staff metrics (workplace)
  // 1) Professional burnout level
  let profLevel = 'Low Risk';
  if (burnoutScore <= 30) profLevel = 'Low Risk';
  else if (burnoutScore <= 60) profLevel = 'Moderate Risk';
  else profLevel = 'High Risk';

  // 2) Emotional well-being indicators
  const indicators = [];
  let concernCount = 0;
  if (stressed > 65 && demotivated > 50) { indicators.push({ type: 'Emotional Exhaustion', severity: 'high' }); concernCount++; }
  if (anxious > 60) { indicators.push({ type: 'Work Anxiety', severity: 'medium' }); concernCount++; }
  if (hurt > 55 && calm < 25) { indicators.push({ type: 'Compassion Fatigue', severity: 'medium' }); concernCount++; }
  if (demotivated > 65 && anxious > 40) { indicators.push({ type: 'Cynicism/Detachment', severity: 'medium' }); concernCount++; }

  // 3) Work engagement & resilience
  const engagementScore = (calm * 0.5) + ((happy || calm) * 0.5);
  const resilienceScore = Math.max(0, Math.min(100, 100 - ((stressed + anxious + demotivated) / 3)));
  let workEngagement = (engagementScore * 0.6) + (resilienceScore * 0.4);
  workEngagement = Math.max(0, Math.min(100, workEngagement));

  let highEngagement = 0, moderate = 0, atRisk = 0;
  if (workEngagement >= 60) {
    highEngagement = Math.round((workEngagement / 100) * 100);
    moderate = 100 - highEngagement;
    atRisk = 0;
  } else if (workEngagement >= 30) {
    moderate = Math.round((workEngagement / 100) * 100);
    highEngagement = 0;
    atRisk = 100 - moderate;
  } else {
    atRisk = Math.round((30 - workEngagement) / 30 * 100);
    atRisk = Math.max(0, Math.min(100, atRisk));
    highEngagement = 0;
    moderate = 100 - atRisk;
  }

  return {
    type: 'staff',
    professionalBurnout: {
      score: burnoutRounded,
      level: profLevel,
      percentage: Math.round((burnoutScore / 100) * 100),
      breakdown: {
        stressed, anxious, hurt, demotivated, raw: Number(burnoutRaw.toFixed(2))
      }
    },
    emotionalIndicators: { indicators, count: concernCount, summaryText: `${concernCount} Concerns Identified` },
    workEngagement: { highEngagement, moderate, atRisk, rawScore: Math.round(workEngagement) }
  };
}
// ...end file...